/**
 * Sticker Slam — chunky sticker word slams in with rotate / flip / drop variant.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const stickerSlam: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const text = inputs.text ?? "NEU";
  const sub = inputs.subtext ?? "";
  const variant = (inputs.variant ?? "rotate").toLowerCase();

  const hero = typeSize("hero", ctx.format);
  const md = typeSize("md", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const stickerId = `${clipPrefix}-st`;
  const shadowId = `${clipPrefix}-sh`;
  const subId = `${clipPrefix}-sub`;
  const burstId = `${clipPrefix}-burst`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const burstSize = ctx.format === "9:16" ? 720 : 820;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:80px;">
    <div style="position:relative;display:flex;align-items:center;justify-content:center;width:${burstSize}px;height:${burstSize}px;">
      <svg id="${burstId}" width="${burstSize}" height="${burstSize}" viewBox="0 0 100 100" style="position:absolute;inset:0;">
        <polygon points="50,2 53.5,30.5 76,11 65,38 96,28 71,48 100,55 70,57 88,82 60,65 64,98 50,72 36,98 40,65 12,82 30,57 0,55 29,48 4,28 35,38 24,11 46.5,30.5" fill="${accent}" stroke="${themeColors.bg}" stroke-width="0.8" stroke-linejoin="round"/>
      </svg>
      <div id="${shadowId}" style="position:absolute;inset:8px;border-radius:50%;background:${withAlpha("#000", 0.25)};filter:blur(20px);"></div>
      <div id="${stickerId}" style="position:relative;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero * 0.95}px;letter-spacing:-4px;color:${themeColors.bg};text-shadow:0 8px 0 ${withAlpha("#000", 0.25)};line-height:0.9;text-align:center;text-wrap:balance;padding:0 24px;">${escapeHtml(text)}</div>
    </div>
    ${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.7}px;letter-spacing:6px;text-transform:uppercase;color:${themeColors.fg};">${escapeHtml(sub)}</div>` : ""}
  </div>`;

  const init = [
    gsapSetLine(`#${burstId}`, variant === "drop" ? { opacity: 0, y: -200, rotation: 0 } : variant === "flip" ? { opacity: 0, rotationY: 180, scale: 0.6 } : { opacity: 0, scale: 0.4, rotation: -25 }),
    gsapSetLine(`#${stickerId}`, { opacity: 0, scale: 0.85 }),
    gsapSetLine(`#${shadowId}`, { opacity: 0, scale: 0.6 }),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${burstId}`, variant === "drop" ? { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"bounce.out\"" } : variant === "flip" ? { opacity: 1, rotationY: 0, scale: 1, duration: 0.7, ease: "raw:\"back.out(1.8)\"" } : { opacity: 1, scale: 1, rotation: -10, duration: 0.7, ease: "raw:\"back.out(2)\"" }, 0.05),
    t.to(`#${stickerId}`, { opacity: 1, scale: 1, duration: 0.45, ease: "raw:\"back.out(2)\"" }, 0.35),
    t.to(`#${shadowId}`, { opacity: 1, scale: 1, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.05),
    t.to(`#${burstId}`, { rotation: variant === "rotate" ? 350 : variant === "drop" ? 360 : 360, duration: clipDurationSec, ease: "raw:\"none\"" }, 0),
    t.to(`#${stickerId}`, { scale: 1.05, duration: 0.5, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 0.8),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.7));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
